/** `W-20b` — **드롭존에 떨어뜨린 것이 웹의 담기다.** 그래서 앱 `A-21b` 와 **같은 순간**이고,
 * 제목·본문 세 문장·버튼 둘·배너 두 줄까지 **내용이 같다**(`docs/PRD.md` `7.7.3`).
 * 배너 끝의 `판정 기준 미정` 배지는 목업의 설계 표기라 앱 `A-21b` 와 함께 걷었다. 판정
 * 기준이 미정이라는 사실은 `docs/PRD.md` 오픈 이슈로 남는다.
 * 규격 정본은 `7.6.5a` 이고 이 절은 **모양만** 다르다 — 폰의 바닥 시트가 아니라
 * **드롭존 위 모달**이다.
 *
 * ## ⛔ 여기서 문장을 다시 고르지 않는다
 *
 * `7.7.3`: *"⛔ 웹에서 문장을 다시 고르지 않는다. 한 사실을 두 클라이언트가 두 낱말로
 * 부르면 그것이 `7.5.9` 규칙 1이 막는 일이다."* 글자는 전부 `@makit/shared` 의
 * `precut-sheet` 에서 온다 — 앱의 `features/capture/precut-sheet.tsx` 가 **같은 함수**를
 * 부른다. ⛔ 이 파일에 한글 문장을 박지 마라.
 *
 * ## ⛔ `ConfirmDialog` 를 쓰지 않는 이유
 *
 * 그쪽은 머리말이 **"되돌릴 수 없는 동작에만 쓴다"** 라고 잠가 둔 자리다. 이 모달은 **아무것도
 * 파괴하지 않는다** — 받기로 한 길을 한 번 알릴 뿐이고, 그래서 primary 가 `danger` 가 아니라
 * **계속 가는 쪽**이다. 그리고 `ConfirmDialog` 에는 제한 배너가 설 자리가 없다.
 *
 * ## ⛔ 드롭존 보조줄에 이 말을 넣지 않는다
 *
 * `7.7.3`: 그 줄은 **늘 서 있는 줄**이라(`7.5.9` 판정 1) **가끔 참인 사실**을 담을 수 없다.
 * **늘 말하는 것과 그때만 말하는 것을 한 줄에 섞으면 둘 다 안 읽힌다.**
 */

import { useEffect, useRef } from "react";
import {
  precutSheetActions,
  precutSheetBody,
  precutSheetDismissOutcome,
  precutSheetLimit,
  precutSheetTitle,
  type PrecutSheetOutcome,
} from "@makit/shared";

import { LimitBanner } from "../../components/banners";
import { Button } from "../../components/primitives";

export interface PrecutModalProps {
  /** 걸린 장의 수. **`countPrecut` 이 낸 값이다.** ⛔ `여러 장` 으로 뭉치지 않는다. */
  count: number;
  /** 셀러가 답했다. **닫는 길 전부가 이 하나로 모인다** — 스크림 누르기 · `Esc` ·
   *  `다시 고르기`. ⛔ 셋을 다른 쪽으로 잇지 마라(`7.6.5a`). */
  onDecide: (outcome: PrecutSheetOutcome) => void;
}

export function PrecutModal({ count, onDecide }: PrecutModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const actions = precutSheetActions();
  const limit = precutSheetLimit();
  const title = precutSheetTitle(count);

  useEffect(() => {
    opener.current = document.activeElement;
    // ⭐ **첫 포커스가 `다시 고르기` 에 간다** — 마크업상 첫 버튼이다. primary 가 계속 가는
    //    쪽인 것과 다투지 않는다: **기본값은 눈으로 읽는 것**(채운 버튼)이고, **키보드로
    //    엔터를 치는 사람이 읽지 않은 채 돈이 드는 쪽으로 가지 않게** 하는 것이 이 줄이다.
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    return () => {
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      // ⛔ **`Esc` 는 `다시 고르기` 와 같은 일이다.** 담는 쪽으로 떨어뜨리면 **안 읽고 닫은
      //    것이 돈이 되는 선택**이 된다(`7.6.5a`).
      if (event.key === "Escape") onDecide(precutSheetDismissOutcome());
      if (event.key !== "Tab" || !ref.current) return;
      const focusable = ref.current.querySelectorAll<HTMLElement>("button, [href], input, [tabindex]");
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onDecide]);

  return (
    <div
      className="scrim"
      // ⛔ **스크림 누르기도 `다시 고르기` 다.** 위 `Esc` 와 같은 값을 쓴다.
      onMouseDown={(e) =>
        e.target === e.currentTarget && onDecide(precutSheetDismissOutcome())
      }
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <span className="st">{title}</span>
        {/* 세 문장이 한 문단으로 흐른다(목업 `.sb`). **순서가 곧 우선순위다.**
            이어 붙이면 175자라 줄글이고, 어절 보존을 걷는다(`styles/base.css` 의 `.prose`). */}
        <span className="sb prose">{precutSheetBody(count).join(" ")}</span>
        {/* **판정이 서면 이 배너가 통째로 사라진다.** 걷는 것은 `design-manager` 소관이다. */}
        <LimitBanner icon="!">
          <span className="l1">{limit.l1}</span>
          <span className="l2">
            {limit.l2}
          </span>
        </LimitBanner>
        {/* 마크업 차례가 **좌 ghost / 우 primary** 다. `.row` 가 `wrap-reverse` 라 쌓이면
            주 동작이 위로 온다(`7.5.1`). */}
        <div className="row">
          <Button
            label={actions.secondary.label}
            variant="ghost"
            onClick={() => onDecide(actions.secondary.outcome)}
          />
          {/* ⭐ **primary 가 계속 가는 쪽이다.** 물러나는 쪽에 두면 화면이 결정과 반대로 말한다. */}
          <Button label={actions.primary.label} onClick={() => onDecide(actions.primary.outcome)} />
        </div>
      </div>
    </div>
  );
}
